if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      auBnplBootstrapInterval = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.setupAuBnplCard();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.auBnplBootstrapInterval) {
          window.clearInterval(this.auBnplBootstrapInterval);
          this.auBnplBootstrapInterval = undefined;
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("modals");
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.setupAuBnplCard();
        };
      }

      shouldUseAuBnplCard() {
        return (
          window.location.hostname.includes("speediance.com.au") &&
          Boolean(
            this.querySelector(
              `[data-bnpl-card][data-section-id="${this.dataset.section}"]`,
            ) || this.querySelector("[data-bnpl-card]"),
          )
        );
      }

      getAuBnplPriceAmount() {
        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return null;

        const priceElement = [
          ".price__sale .price-item--sale",
          ".price__sale .price-item--last",
          ".price-item--sale",
          ".price__regular .price-item--regular",
          ".price-item--regular",
        ]
          .map((selector) => priceRoot.querySelector(selector))
          .find((node) => node && node.textContent.trim().length);

        const raw = (priceElement?.textContent || priceRoot.textContent || "")
          .replace(/[^0-9,.\-]/g, "");
        if (!raw) return null;

        const lastComma = raw.lastIndexOf(",");
        const lastDot = raw.lastIndexOf(".");
        const normalized =
          lastComma > lastDot
            ? raw.replace(/\./g, "").replace(",", ".")
            : raw.replace(/,/g, "");

        const amount = Number.parseFloat(normalized);
        return Number.isFinite(amount) ? amount : null;
      }

      formatAuBnplMoney(value, currencyCode) {
        try {
          return new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        } catch (error) {
          return `${currencyCode} ${value.toFixed(2)}`;
        }
      }

      ensureAuBnplStyles() {
        if (document.getElementById("au-bnpl-card-styles")) return;

        const style = document.createElement("style");
        style.id = "au-bnpl-card-styles";
        style.textContent = `
/* Buy Now, Pay Later widget (Humm + Splitit) — Speediance AU brand build 2026-06-06.
   On-brand (Jet Black #0A0A0A + #FF5733, Helvetica Now), larger type, two
   equal tiles. Humm keeps its OFFICIAL humm-au.com representative-example
   widget (regulated credit product — must show real terms/total payable), so
   it is styled-up rather than replaced. Splitit shows our own large monthly
   amount with the official spt-one-liner kept in an expandable panel for its
   Learn-more. NOTE: no blanket "interest-free" claim — only Splitit is
   interest-free; Humm carries a cost (shown in its widget). */

.bnpl-card {
  position: relative;
  font-family: 'Helvetica Now Var', 'Helvetica Now', Helvetica, Arial, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  max-width: 40rem;
  margin-top: 1.2rem;
  padding: 1.4rem 1.6rem;
  background: #fff;
  border: 0.1rem solid rgba(10, 10, 10, 0.10);
  border-radius: 1.2rem;
  box-shadow: 0 0.1rem 0.3rem rgba(10, 10, 10, 0.05);
}

.bnpl-card__title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #0A0A0A;
}

.bnpl-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.bnpl-plan {
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
}
.bnpl-plan + .bnpl-plan {
  border-top: 0.1rem solid rgba(10, 10, 10, 0.08);
}

.bnpl-plan__brand {
  margin: 0 0 0.6rem;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #FF5733;
}

/* ---- Splitit (our own large amount) ---- */
.bnpl-splitit { list-style: none; }
.bnpl-splitit > .bnpl-splitit__summary { list-style: none; cursor: pointer; display: block; }
.bnpl-splitit > .bnpl-splitit__summary::-webkit-details-marker { display: none; }

.bnpl-plan__amount {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.bnpl-plan__num {
  font-size: 2.1rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
  color: #0A0A0A;
}
.bnpl-plan__per {
  font-size: 1.2rem;
  font-weight: 600;
  color: #0A0A0A;
  opacity: 0.4;
}
.bnpl-plan__term {
  margin: 0.6rem 0 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(10, 10, 10, 0.75);
}
.bnpl-plan__caption {
  margin: 0.4rem 0 0;
  font-size: 1.05rem;
  line-height: 1.45;
  color: rgba(10, 10, 10, 0.5);
}
.bnpl-learn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
  color: #FF5733;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
  white-space: nowrap;
}
.bnpl-learn::after {
  content: "";
  width: 0.5rem;
  height: 0.5rem;
  border-right: 0.16rem solid currentColor;
  border-bottom: 0.16rem solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
  margin-left: 0.1rem;
}
.bnpl-splitit[open] .bnpl-learn::after { transform: rotate(-135deg); }

.bnpl-splitit-panel { margin-top: 0.8rem; }
.bnpl-splitit-target { min-height: 3rem; display: block; width: 100%; }
.bnpl-splitit-target spt-one-liner,
.bnpl-splitit-target [id^="upstream_messaging_"] { display: block; width: 100%; }

/* ---- Humm (official widget, styled up) ---- */
.bnpl-humm-caption {
  margin: 0 0 0.7rem;
  font-size: 1.1rem;
  line-height: 1.4;
  font-weight: 600;
  color: rgba(10, 10, 10, 0.78);
}
.bnpl-humm-caption strong {
  font-weight: 800;
  color: #0A0A0A;
}
.bnpl-humm { min-height: 3rem; min-width: 0; }
.bnpl-humm:empty::before {
  content: "Loading Humm options...";
  font-size: 1.1rem;
  color: rgba(10, 10, 10, 0.4);
}
.bnpl-humm .humm-price-info-widget { display: block; }
.bnpl-humm .humm-description,
.bnpl-humm .humm-main {
  font-size: 1.2rem;
  line-height: 1.5;
  color: rgba(10, 10, 10, 0.7);
  font-weight: 500;
}
.bnpl-humm .humm-price {
  font-weight: 800;
  color: #0A0A0A;
}
.bnpl-humm .humm-more-info {
  color: #FF5733;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}
.bnpl-humm .humm-widget-logo {
  max-height: 1.6rem;
  width: auto;
  margin-top: 0.5rem;
}

.bnpl-foot {
  margin: 0;
  font-size: 1rem;
  line-height: 1.45;
  color: rgba(10, 10, 10, 0.4);
}

/* Humm has a per-merchant amount cap; above it the official widget renders
   nothing. In that case hide the Humm row entirely (Splitit stays). */
.bnpl-no-humm .bnpl-plan--humm { display: none; }
.bnpl-no-humm .bnpl-splitit { border-top: 0; }

@media (max-width: 749px) {
  .bnpl-card {
    max-width: 100%;
    padding: 1.2rem;
    gap: 1rem;
  }
  .bnpl-plan__num { font-size: 2rem; }
}

        `;
        document.head.appendChild(style);
      }

      ensureAuBnplMarkup() {
        const existing = this.querySelector(
          `[data-bnpl-card][data-section-id="${this.dataset.section}"]`,
        );
        const compactMarkup = `
          <h2 class="bnpl-card__title">Buy now, pay later with Humm or Splitit</h2>
          <div class="bnpl-grid">
            <div class="bnpl-plan bnpl-plan--humm">
              <p class="bnpl-plan__brand">Humm</p>
              <p class="bnpl-humm-caption">Pay <strong>20% upfront</strong>, then the rest on finance in fortnightly instalments over 30 months. Approval and fees apply.</p>
              <div class="bnpl-humm" data-humm-target></div>
            </div>
            <details class="bnpl-plan bnpl-splitit" data-splitit-disclosure>
              <summary class="bnpl-splitit__summary">
                <p class="bnpl-plan__brand">Splitit</p>
                <p class="bnpl-plan__amount"><span class="bnpl-plan__num" data-splitit-monthly>$--.--</span><span class="bnpl-plan__per">/month</span></p>
                <p class="bnpl-plan__term">over 12 months, interest-free</p>
                <p class="bnpl-plan__caption">On your existing credit card &middot; <span class="bnpl-learn">Learn more</span></p>
              </summary>
              <div class="bnpl-splitit-panel">
                <div class="bnpl-splitit-target" id="installment-target" data-installment-target></div>
              </div>
            </details>
          </div>
          <p class="bnpl-foot">Choose Humm or Splitit at checkout. Humm subject to approval; representative example shown above.</p>
        `;

        if (existing) {
          if (
            !existing.querySelector(".bnpl-plan") ||
            !existing.querySelector("[data-humm-target]")
          ) {
            existing.innerHTML = compactMarkup;
          }
          return existing;
        }

        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return null;

        const card = document.createElement("div");
        card.className = "bnpl-card color-scheme-1";
        card.dataset.bnplCard = "true";
        card.dataset.sectionId = this.dataset.section;
        card.dataset.currencyCode =
          window?.Shopify?.currency?.active || "AUD";
        card.innerHTML = compactMarkup;

        priceRoot.insertAdjacentElement("afterend", card);
        return card;
      }

      auHummIsViable(amount) {
        // Humm AU account 30146926 exposes a single 65-fortnight (30-month)
        // plan that adds a flat ~$358.50 in fees ($60 establishment +
        // $9.95/month x 30) to ANY purchase. On big-ticket items that is a
        // small share, but on low-priced products it inflates "total payable"
        // into nonsense (e.g. $399 -> $757.50). Only surface Humm where the
        // fixed fees stay a sensible portion of the price; Splitit still shows
        // below this cutoff.
        const HUMM_MIN_AMOUNT = 1500;
        return Number.isFinite(amount) && amount >= HUMM_MIN_AMOUNT;
      }

      mountAuHummWidget(target, amount) {
        if (!target) return;

        if (
          target.dataset.hummAmount === String(amount) &&
          (target.querySelector(".humm-price-info-widget") ||
            target.querySelector("script"))
        ) {
          return;
        }

        target.innerHTML = "";
        target.dataset.hummAmount = String(amount);

        const script = document.createElement("script");
        script.src = `https://bpi.humm-au.com/au/content/scripts/price-info_sync.js?productPrice=${amount}&merchantId=30146926`;
        script.async = true;
        target.appendChild(script);
      }

      syncAuBnplCard() {
        if (!this.shouldUseAuBnplCard()) return false;

        this.ensureAuBnplStyles();
        const card = this.ensureAuBnplMarkup();
        if (!card) return false;

        const amount = this.getAuBnplPriceAmount();
        if (!amount) return false;

        const currencyCode = card.dataset.currencyCode || "AUD";
        const monthlyNode = card.querySelector("[data-splitit-monthly]");
        const splititTarget = card.querySelector("[data-installment-target]");
        const hummTarget = card.querySelector("[data-humm-target]");

        if (monthlyNode) {
          monthlyNode.textContent = this.formatAuBnplMoney(
            amount / 12,
            currencyCode,
          );
        }

        const titleNode = card.querySelector(".bnpl-card__title");
        const footNode = card.querySelector(".bnpl-foot");

        if (this.auHummIsViable(amount)) {
          if (titleNode)
            titleNode.textContent = "Buy now, pay later with Humm or Splitit";
          if (footNode)
            footNode.textContent =
              "Choose Humm or Splitit at checkout. Humm subject to approval; representative example shown above.";

          this.mountAuHummWidget(hummTarget, amount);

          if (
            hummTarget &&
            hummTarget.querySelector(".humm-price-info-widget")
          ) {
            card.classList.remove("bnpl-no-humm");
          }
        } else {
          card.classList.add("bnpl-no-humm");
          if (hummTarget) {
            hummTarget.innerHTML = "";
            delete hummTarget.dataset.hummAmount;
          }
          if (titleNode)
            titleNode.textContent = "Buy now, pay later with Splitit";
          if (footNode)
            footNode.textContent =
              "Choose Splitit at checkout. Pay in 12 interest-free monthly instalments on your existing credit card, subject to approval.";
        }

        if (
          splititTarget &&
          typeof window.upstream_messaging !== "undefined" &&
          !splititTarget.querySelector('[id^="upstream_messaging_"]')
        ) {
          if (
            splititTarget.dataset.splititAmount !== String(amount) ||
            !splititTarget.querySelector("spt-one-liner")
          ) {
            splititTarget.innerHTML = `
              <spt-one-liner
                vis="true"
                amount="${amount}"
                installments="12"
                logo_color="#FF5733"
                learn_more_color="#FF5733"
              ></spt-one-liner>
            `;
            splititTarget.dataset.splititAmount = String(amount);
          }
        }

        return true;
      }

      setupAuBnplCard() {
        if (!this.shouldUseAuBnplCard()) return;

        this.syncAuBnplCard();

        if (this.auHummCheckTimeout) window.clearTimeout(this.auHummCheckTimeout);
        this.auHummCheckTimeout = window.setTimeout(() => {
          const card = this.querySelector(
            `[data-bnpl-card][data-section-id="${this.dataset.section}"]`,
          );
          const hummTarget = card && card.querySelector("[data-humm-target]");
          if (card && !(hummTarget && hummTarget.querySelector(".humm-price-info-widget"))) {
            card.classList.add("bnpl-no-humm");
          }
        }, 9000);

        if (this.auBnplBootstrapInterval) {
          window.clearInterval(this.auBnplBootstrapInterval);
        }

        this.auBnplBootstrapInterval = window.setInterval(() => {
          if (!this.isConnected) {
            window.clearInterval(this.auBnplBootstrapInterval);
            this.auBnplBootstrapInterval = undefined;
            return;
          }

          this.syncAuBnplCard();

          const card = this.querySelector(
            `[data-bnpl-card][data-section-id="${this.dataset.section}"]`,
          );
          const splititTarget = card?.querySelector("[data-installment-target]");
          const hummTarget = card?.querySelector("[data-humm-target]");
          if (
            splititTarget &&
            hummTarget &&
            (splititTarget.querySelector("spt-one-liner") ||
              splititTarget.querySelector('[id^="upstream_messaging_"]')) &&
            (!this.auHummIsViable(this.getAuBnplPriceAmount()) ||
              hummTarget.querySelector(".humm-price-info-widget"))
          ) {
            window.clearInterval(this.auBnplBootstrapInterval);
            this.auBnplBootstrapInterval = undefined;
          }
        }, 1000);

        window.setTimeout(() => {
          if (this.auBnplBootstrapInterval) {
            window.clearInterval(this.auBnplBootstrapInterval);
            this.auBnplBootstrapInterval = undefined;
          }
        }, 30000);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === ".quantity__label") {
              const updatedAriaLabelledBy =
                updated.getAttribute("aria-labelledby");
              if (updatedAriaLabelledBy) {
                current.setAttribute("aria-labelledby", updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}

